import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useChartDateBarContext } from '../../context'
import { Resolution, ResolutionTypes } from '../../types'
import { getAvailableResolutions, getResolutionLabel } from '../../utils/csv-export-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface ReturnType {
  menuAnchor: HTMLElement | null
  onMenuClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void
  handleMenuOpen: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  resolutionMenuItems: Array<{
    resolution: Resolution
    label: string
    onClick: () => void
  }>
  resolution: Resolution
}

/**
 * Hook to manage available resolutions for exporting chart data based on the current chart view and date range.
 * Provides state and handlers for a menu to select the resolution.
 */
const useResolutions = (): ReturnType => {
  const { t } = useCustomTranslation()
  const { view, from, to, hasCustomRange } = useChartDateBarContext()
  const customRangeInDays = to.diff(from, 'days') + 1
  const resolutions = useMemo(() => getAvailableResolutions(view, hasCustomRange, customRangeInDays), [view, hasCustomRange, customRangeInDays])
  const [resolution, setResolution] = useState<Resolution>(ResolutionTypes.Day)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (view === 'day' && resolution !== '15min') setResolution('15min')
    if (view === 'week' && resolution !== 'hour') setResolution('hour')
    if (view === 'month' && resolution !== 'day') setResolution('day')
    if (view === 'year' && resolution !== 'month') setResolution('month')
  }, [view, resolution])

  const onMenuClose = useCallback((event?: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    if (event) {
      event.stopPropagation()
    }
    setMenuAnchor(null)
  }, [])

  const handleMenuSelect = useCallback(
    (resolution: Resolution): void => {
      setResolution(resolution)
      onMenuClose()
    },
    [onMenuClose]
  )

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setMenuAnchor(event?.currentTarget)
  }, [])

  const resolutionMenuItems = useMemo(
    () =>
      resolutions.map(resolution => ({
        resolution,
        label: getResolutionLabel(resolution, t),
        onClick: () => handleMenuSelect(resolution),
      })),
    [resolutions, handleMenuSelect, t]
  )

  return { menuAnchor, onMenuClose, handleMenuOpen, resolutionMenuItems, resolution }
}

export default useResolutions
